import IconPencil from "assets/img/icon-pencil.svg?react"

import React, { FunctionComponent } from "react"
import { useTranslation } from "react-i18next"

import { Grid, GridItem } from "components/Grid"
import { Flex } from "components/Flex"
import Text from "components/Text/Text"
import { ISummarySection } from "@/types"
import ListKeyValue from "components/List/ListKeyValue"
import { IListItem } from "components/List/List"
import Button from "components/Button/Button"
import Status from "components/Status/Status"

type ISummaryProps = {
    data: ISummarySection<IListItem>[]
    condensed?: boolean
}

const Summary: FunctionComponent<React.PropsWithChildren<ISummaryProps>> = ({
    data,
    condensed,
}) => {
    const { t } = useTranslation()

    if (data.length === 0) return null

    function renderSection(section: ISummarySection<IListItem>, isSub?: boolean) {
        return (
            <>
                {(!!section.title || !!section.onEditClick || section.editLink) &&
                    <Flex
                        className="mb-2"
                        alignItems="center"
                        justifyContent="space-between"
                        gap="1.6rem">
                        {!!section.title &&
                            <Text type={isSub ? "h4" : "h3"}>{section.title}</Text>
                        }
                        <Flex alignItems="center"
                            gap="1.6rem">
                            {section.toolbar}
                            {!!section.editLink &&
                                <Button
                                    type="link"
                                    url={section.editLink}
                                    theme="transparent"
                                    round
                                    icon={<IconPencil />}
                                    ariaLabel={t('common.edit')}
                                />
                            }
                            {!!section.onEditClick &&
                                <Button
                                    onClick={section.onEditClick}
                                    theme="grey"
                                    round
                                    icon={<IconPencil />}
                                    ariaLabel={t('common.edit')}
                                />
                            }
                        </Flex>
                    </Flex>
                }
                {!!section.status &&
                    <div className="mb-2">
                        <Status type={section.status.type}>
                            {section.status.message}
                        </Status>
                    </div>
                }
                {!!section.subtitle &&
                    <div className="mb-2 color-grey-500">
                        {section.subtitle}
                    </div>
                }
                <ListKeyValue items={section.items} />
            </>
        )
    }

    return (
        <Grid columns="1fr" gap={condensed ? "3.2rem" : "6.4rem"}>
            {data?.map((section, sectionIndex) => (
                <GridItem key={sectionIndex}>
                    {renderSection(section)}
                    {!!section.subSections && section.subSections.length > 0 &&
                        <Grid columns="1fr" gap="3.6rem" className="mt-4">
                            {section.subSections.map((subSection, subSectionIndex) => (
                                <GridItem key={subSectionIndex}>
                                    {renderSection(subSection, true)}
                                </GridItem>
                            ))}
                        </Grid>
                    }
                </GridItem>
            ))}
        </Grid>
    )
}

export default Summary
